import type { Feature, Point } from 'geojson';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo, useState } from 'react';
import './regional-overview-mycotoxin-fields-info-popup-container.less';

export interface RegionalOverviewMycotoxinFieldsClusteredContainerProps {
  mapboxPopup: mapboxgl.Popup;
  data: Feature<Point, RegionalOverviewProperties>[];
  clusterCount: number;
  currentUnitSystem: string;
  onFieldPopupOpened?: () => void;
}

interface MycotoxinFieldsInfoPopupBloc {
  orderedData: Feature<Point, RegionalOverviewProperties>[];
  hasClusterData: boolean;
  selectedField?: Feature<Point, RegionalOverviewProperties>;
  handleSelectField: (field: Feature<Point, RegionalOverviewProperties>) => void;
  handleClosePopup: () => void;
  clusterCount: number;
  currentUnitSystem: string;
}

function sortByPropertyName(a: Feature<Point, RegionalOverviewProperties>, b: Feature<Point, RegionalOverviewProperties>): number {
  if (a.properties.property_name && b.properties.property_name) {
    return a.properties.property_name.localeCompare(b.properties.property_name);
  }
  return a.properties.name.localeCompare(b.properties.name);
}

export const useMycotoxinFieldsInfoPopupBloc = ({
  mapboxPopup,
  data,
  clusterCount,
  currentUnitSystem,
  onFieldPopupOpened
}: RegionalOverviewMycotoxinFieldsClusteredContainerProps): MycotoxinFieldsInfoPopupBloc => {
  const hasClusterData = data.length > 1;
  const [selectedField, setSelectedField] = useState<Feature<Point, RegionalOverviewProperties> | undefined>(
    !hasClusterData ? data[0] : undefined
  );

  const handleSelectField = (field: Feature<Point, RegionalOverviewProperties> | undefined) => {
    setSelectedField(field);

    if (field) {
      onFieldPopupOpened?.();
    }
  };

  const orderedData = useMemo<Feature<Point, RegionalOverviewProperties>[]>(() => {
    return [...data].sort((a, b) => {
      const sortByHigherRisk = Number.parseInt(b.properties.risk, 10) - Number.parseInt(a.properties.risk, 10);
      return sortByHigherRisk || sortByPropertyName(a, b);
    });
  }, [data]);

  const handleClosePopup = () => {
    mapboxPopup.remove();
  };

  return {
    orderedData,
    hasClusterData,
    selectedField,
    handleSelectField,
    handleClosePopup,
    clusterCount,
    currentUnitSystem
  };
};
